import { useTestStore, TV_TESTS, MOBILE_TESTS } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { ScoreChip } from '@/components/ScoreChip';
import { MetricBox } from '@/components/MetricBox';
import { fmtInt } from '@/utils/format';

const TV_NAV_KEYS: Record<string, string> = {
  'tv-list': 'tv.list.title',
  'tv-css': 'tv.css.title',
  'tv-compute': 'tv.compute.title',
  'tv-memory': 'tv.memory.title',
  'tv-canvas2d': 'tv.canvas.title',
  'tv-webgl': 'tv.webgl.title',
  'tv-events': 'tv.events.title',
  'tv-network': 'tv.network.title',
};

const MOBILE_NAV_KEYS: Record<string, string> = {
  'mobile-touch': 'mobile.touch.title',
  'mobile-viewport': 'mobile.viewport.title',
  'mobile-scroll': 'mobile.scroll.title',
  'mobile-media': 'mobile.media.title',
  'mobile-battery': 'mobile.battery.title',
  'mobile-pwa': 'mobile.pwa.title',
  'mobile-form': 'mobile.form.title',
  'mobile-network': 'mobile.network.title',
};

export function Summary() {
  const { language, deviceMode, results, getOverallScore, getVerdict } = useTestStore();
  const tests = deviceMode === 'tv' ? TV_TESTS : MOBILE_TESTS;
  const navKeys = deviceMode === 'tv' ? TV_NAV_KEYS : MOBILE_NAV_KEYS;

  const overallScore = getOverallScore();
  const verdict = getVerdict();

  const completedCount = tests.filter((id) => results[id] != null).length;

  return (
    <div className="border border-border rounded-[18px] bg-white/[.02] p-4">
      <h2 className="text-[22px] font-black tracking-tight mb-1.5">
        {t('summary.title', language)}
      </h2>
      <p className="text-sm text-muted leading-snug mb-3.5">
        {t('summary.desc', language)}
      </p>

      <div className="grid grid-cols-4 gap-2.5 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox
          label={t('summary.overall', language)}
          value={overallScore != null ? String(overallScore) : '—'}
          hint={language === 'pl' ? 'Średnia ważona' : 'Weighted average'}
        />
        <MetricBox
          label={t('summary.verdict', language)}
          value={t(verdict, language)}
        />
        <MetricBox
          label="WebGL"
          value={
            deviceMode === 'tv' && results['tv-webgl']
              ? results['tv-webgl'].primary
              : '—'
          }
        />
        <MetricBox
          label={t('summary.completed', language)}
          value={`${fmtInt(completedCount)} / ${tests.length}`}
        />
      </div>

      <div className="h-px bg-border my-3.5" />

      <div className="border border-border rounded-2xl overflow-hidden bg-black/[.16]">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">{t('summary.test', language)}</th>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">{t('summary.score', language)}</th>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">{t('summary.result', language)}</th>
            </tr>
          </thead>
          <tbody>
            {tests.map((testId) => {
              const result = results[testId];
              return (
                <tr key={testId} className="border-b border-white/5">
                  <td className="text-sm p-3">{t(navKeys[testId], language)}</td>
                  <td className="text-sm p-3">
                    <ScoreChip score={result?.score ?? null} />
                  </td>
                  <td className="text-sm p-3 text-muted">
                    {result?.primary ?? t('summary.notRun', language)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
